/**
 * The two things that need a machine that can read or listen.
 *
 * Translating a message and writing down a voice note. Both are optional on
 * the server - the host points Pugo at a translator and a transcriber they
 * have chosen - so both are asked about once and the buttons simply do not
 * appear where nothing is configured. A button that apologises is worse than
 * no button.
 *
 * Nothing happens automatically. A message is written in the language
 * somebody chose and a recording is somebody's voice; replacing either
 * without being asked is not ours to do.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Attachment, Message } from '@/lib/types';
import { cn } from '@/lib/cn';
import { useSession } from '@/stores/session';
import { Icon } from '@/icons/Icon';
import { EmojiText } from './EmojiText';
import { useToast } from './ui';

export type AiCaps = {
  translate: boolean;
  transcribe: boolean;
  translateProvider: string | null;
  transcribeProvider: string | null;
};

/** What this server can do. Asked once and kept - it does not change. */
export function useAi() {
  return useQuery({
    queryKey: ['ai'],
    queryFn: () => api.get<AiCaps>('/api/ai'),
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000
  });
}

/* ------------------------------------------------------------ translation */

export function Translated({ msg }: { msg: Message }) {
  const ai = useAi();
  const settings = useSession((s) => s.settings);
  const toast = useToast();
  const [text, setText] = useState('');
  const [from, setFrom] = useState('');
  const [busy, setBusy] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);

  if (!ai.data?.translate || !msg.body?.trim()) return null;

  async function run() {
    setBusy(true);
    try {
      const res = await api.post<{ text: string; from?: string; provider: string }>('/api/translate', {
        text: msg.body,
        to: (settings?.appLanguage || 'en').slice(0, 2)
      });
      if (!res.text || res.text.trim() === msg.body.trim()) {
        toast('That is already in your language', 'good');
        setText('');
      } else {
        setText(res.text);
        setFrom(res.from || '');
      }
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  if (!text) {
    return (
      <button
        type="button"
        disabled={busy}
        onClick={() => void run()}
        className="mt-0.5 flex items-center gap-1 text-[11.5px] font-semibold text-mute outline-none transition-colors hover:text-accent focus-visible:text-accent"
      >
        <Icon name="translate" size={12} />
        {busy ? 'Translating…' : 'Translate'}
      </button>
    );
  }

  return (
    <div className="mt-1 rounded-[calc(10px*var(--radius-mul,1))] bg-surface-2 px-2.5 py-1.5">
      <p className="m-0 text-[13.5px] text-ink">
        <EmojiText text={showOriginal ? msg.body : text} size={16} />
      </p>
      <div className="mt-1 flex items-center gap-2 text-[11px] text-ghost">
        <Icon name="translate" size={11} />
        <span>
          {showOriginal ? 'Original' : 'Translated' + (from ? ' from ' + from.toUpperCase() : '')}
          {ai.data.translateProvider ? ' by ' + ai.data.translateProvider : ''}
        </span>
        <button
          type="button"
          onClick={() => setShowOriginal((v) => !v)}
          className="font-semibold text-mute transition-colors hover:text-ink"
        >
          {showOriginal ? 'Show translation' : 'Show original'}
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ transcripts */

export function Transcript({ msg, attachment }: { msg: Message; attachment: Attachment }) {
  const ai = useAi();
  const toast = useToast();
  const cached = msg.transcripts?.[attachment.id]?.text ?? '';
  const [text, setText] = useState(cached);
  const [busy, setBusy] = useState(false);

  if (!ai.data?.transcribe) return null;

  async function run() {
    setBusy(true);
    try {
      const res = await api.post<{ text: string }>('/api/transcribe', { messageId: msg.id, attachmentId: attachment.id });
      setText(res.text || '(nothing could be made out)');
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  if (!text) {
    return (
      <button
        type="button"
        disabled={busy}
        onClick={() => void run()}
        className="mt-1 flex items-center gap-1 text-[11.5px] font-semibold text-mute outline-none transition-colors hover:text-accent"
      >
        <Icon name="translate" size={12} />
        {busy ? 'Listening…' : 'Write it down'}
      </button>
    );
  }

  return (
    <div className="mt-1.5 rounded-[calc(10px*var(--radius-mul,1))] bg-surface-2 px-2.5 py-2">
      <p className="m-0 text-[13px] leading-relaxed text-dim">{text}</p>
      <p className="m-0 mt-1 text-[11px] text-ghost">
        Written down{ai.data.transcribeProvider ? ' by ' + ai.data.transcribeProvider : ''}. It can be wrong; the recording is what was said.
      </p>
    </div>
  );
}

/* --------------------------------------------------------- notes on a clip */

export type Mark = {
  id: string;
  attachmentId: string;
  at: number;
  body: string;
  by: string;
  createdAt: number;
  user?: { id: string; displayName: string } | null;
};

/** "1:24" */
function clock(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');
}

/**
 * Comments pinned to a moment in a clip.
 *
 * "Watch what he does at twelve seconds" three messages later is a comment
 * nobody can line up with the video. These carry the moment with them, and
 * clicking one moves the player there.
 */
export function ClipMarks({ msg, attachment, video }: {
  msg: Message;
  attachment: Attachment;
  /** The player to jump, when there is one on screen. */
  video: React.RefObject<HTMLVideoElement | null>;
}) {
  const me = useSession((s) => s.me);
  const toast = useToast();
  const [marks, setMarks] = useState<Mark[]>(() => (msg.marks ?? []).filter((m) => m.attachmentId === attachment.id));
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);

  async function add() {
    const at = video.current?.currentTime ?? 0;
    if (!body.trim()) return;
    setBusy(true);
    try {
      const res = await api.post<{ marks: Mark[] }>('/api/messages/' + msg.id + '/marks', {
        attachmentId: attachment.id,
        at,
        body: body.trim()
      });
      setMarks(res.marks.filter((m) => m.attachmentId === attachment.id));
      setBody('');
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    try {
      const res = await api.del<{ marks: Mark[] }>('/api/messages/' + msg.id + '/marks/' + id);
      setMarks(res.marks.filter((m) => m.attachmentId === attachment.id));
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 text-[11.5px] font-semibold text-mute outline-none transition-colors hover:text-accent"
      >
        <Icon name="pin" size={12} />
        {marks.length ? marks.length + (marks.length === 1 ? ' note' : ' notes') + ' on this clip' : 'Note a moment'}
      </button>

      {open ? (
        <div className="mt-1.5 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 p-2.5">
          <ul className="m-0 flex list-none flex-col gap-1 p-0">
            {marks.map((m) => (
              <li key={m.id} className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => { if (video.current) { video.current.currentTime = m.at; void video.current.play().catch(() => {}); } }}
                  className="flex-none rounded-full bg-accent-soft px-2 py-[2px] font-mono text-[11.5px] font-bold text-accent outline-none transition-colors hover:bg-accent hover:text-accent-fg"
                >
                  {clock(m.at)}
                </button>
                <span className="min-w-0 flex-1 truncate text-[12.5px] text-dim">
                  <span className="font-semibold text-ink">{m.user?.displayName ?? 'Somebody'}</span>{' '}
                  {m.body}
                </span>
                {m.by === me?.id || msg.userId === me?.id ? (
                  <button
                    type="button"
                    aria-label="Remove this note"
                    onClick={() => void remove(m.id)}
                    className="grid h-6 w-6 flex-none place-items-center rounded-full text-ghost transition-colors hover:bg-surface-3 hover:text-ink"
                  >
                    <Icon name="close" size={11} />
                  </button>
                ) : null}
              </li>
            ))}
          </ul>

          <div className="mt-2 flex items-center gap-1.5">
            <span className="flex-none rounded-full bg-surface-3 px-2 py-[3px] font-mono text-[11.5px] font-bold text-dim">
              {clock(video.current?.currentTime ?? 0)}
            </span>
            <input
              value={body}
              onChange={(e) => setBody(e.target.value.slice(0, 200))}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void add(); } }}
              placeholder="What happens here?"
              className={cn(
                'h-8 min-w-0 flex-1 rounded-[9px] bg-surface-1 px-2.5 text-[12.5px] outline-none',
                'shadow-[inset_0_0_0_1px_var(--line-soft)] placeholder:text-ghost focus:shadow-[inset_0_0_0_1px_var(--accent)]'
              )}
            />
            <button
              type="button"
              disabled={busy || !body.trim()}
              onClick={() => void add()}
              className="h-8 flex-none rounded-[9px] bg-accent px-2.5 text-[12px] font-bold text-accent-fg disabled:opacity-40"
            >
              Note
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
